import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";

import {
  RiskManagementOverallRiskBarChartsDocument,
  type RiskManagementOverallRiskBarChartsQuery,
  type RiskManagementOverallRiskBarChartsQueryVariables,
} from "$/__generated__/graphql";

import {
  convertPostMitigationBarDataToSeries,
  appendPostMitigationRiskTooltipExtras,
  appendPostMitigationSubgroups,
} from "./utils";
import { useGlobalFiltersContext } from "contexts/filters/global-filters/context";
import { useRiskManagementFiltersContext } from "contexts/filters/risk-management-filters";
import {
  type GeneralBarDatum,
  storageFormatters,
} from "@jana-technology/platform-ui";
import { usePublishedRiskUnitFormatter } from "../usePublishedRiskUnitFormatter";

export const useMitigationRiskCharts = (
  options: useQuery.Options<
    RiskManagementOverallRiskBarChartsQuery,
    RiskManagementOverallRiskBarChartsQueryVariables
  >,
) => {
  const overallRiskChartsQueryResult = useQuery(
    RiskManagementOverallRiskBarChartsDocument,
    options,
  );
  const riskUnitFormatter = usePublishedRiskUnitFormatter();

  const { appliedWellName, appliedHierarchyName, riskMitigationQueryResult } =
    useGlobalFiltersContext();
  const { appliedRiskMitigationId } = useRiskManagementFiltersContext();

  const { riskMitigations } = riskMitigationQueryResult.data ?? {};

  const riskMitigationName =
    riskMitigations?.find(
      (mitigation) => mitigation?.id === appliedRiskMitigationId,
    )?.value ?? "";

  const {
    mitigationRiskByThreatSeries,
    mitigationRiskByConsequenceSeries,
    mitigationRiskByOutcomeScenarioSeries,
    mitigationRiskDataKeys,
  } = useMemo(() => {
    let mitigationRiskByThreatSeries: Array<GeneralBarDatum> = [];
    let mitigationRiskByConsequenceSeries: Array<GeneralBarDatum> = [];
    let mitigationRiskByOutcomeScenarioSeries: Array<GeneralBarDatum> = [];

    if (overallRiskChartsQueryResult.data?.riskManagementChartsForWellNode) {
      const {
        overallRiskByThreat,
        overallRiskByConsequence,
        overallRiskByOutcomeScenario,
      } = overallRiskChartsQueryResult.data.riskManagementChartsForWellNode;

      mitigationRiskByThreatSeries =
        convertPostMitigationBarDataToSeries(overallRiskByThreat);

      mitigationRiskByConsequenceSeries = convertPostMitigationBarDataToSeries(
        overallRiskByConsequence,
      );

      mitigationRiskByOutcomeScenarioSeries =
        convertPostMitigationBarDataToSeries(
          overallRiskByOutcomeScenario,
          "inTotal",
        );

      const tooltipTitle = appliedWellName
        ? `${appliedWellName} (${appliedHierarchyName})`
        : "";

      const baselineTotal = overallRiskByThreat.reduce(
        (acc, curr) => acc + (curr.opTotal ?? 0),
        0,
      );

      const postMitigationTotal = overallRiskByThreat.reduce(
        (acc, curr) => acc + (curr.postMitigation ?? 0),
        0,
      );

      for (const threatDatum of mitigationRiskByThreatSeries) {
        threatDatum.title = tooltipTitle;
        threatDatum.formatter = riskUnitFormatter;
        appendPostMitigationRiskTooltipExtras(
          riskMitigationName,
          baselineTotal,
          postMitigationTotal,
          threatDatum,
          riskUnitFormatter,
        );
      }

      for (let i = 0; i < mitigationRiskByConsequenceSeries.length; i++) {
        mitigationRiskByConsequenceSeries[i].title = tooltipTitle;
        mitigationRiskByConsequenceSeries[i].formatter = riskUnitFormatter;

        appendPostMitigationSubgroups(
          [overallRiskByConsequence[i]],
          mitigationRiskByConsequenceSeries[i],
          "Consequence Type by Category",
          riskUnitFormatter,
        );

        appendPostMitigationRiskTooltipExtras(
          riskMitigationName,
          baselineTotal,
          postMitigationTotal,
          mitigationRiskByConsequenceSeries[i],
          riskUnitFormatter,
        );
      }

      for (let i = 0; i < mitigationRiskByOutcomeScenarioSeries.length; i++) {
        mitigationRiskByOutcomeScenarioSeries[i].title = tooltipTitle;
        mitigationRiskByOutcomeScenarioSeries[i].formatter = riskUnitFormatter;
        appendPostMitigationRiskTooltipExtras(
          riskMitigationName,
          baselineTotal,
          postMitigationTotal,
          mitigationRiskByOutcomeScenarioSeries[i],
          riskUnitFormatter,
        );
        mitigationRiskByOutcomeScenarioSeries[i].tooltipExtras =
          mitigationRiskByOutcomeScenarioSeries[i].tooltipExtras?.concat([
            {
              label: `Baseline LOF (${overallRiskByOutcomeScenario[i].group})`,
              value: storageFormatters.scientificNotation(
                overallRiskByOutcomeScenario[i]?.opLof ?? 0,
              ),
              subGroup: "Baseline",
            },
            {
              label: `Post-Mitigation LOF (${overallRiskByOutcomeScenario[i].group})`,
              value: storageFormatters.scientificNotation(
                overallRiskByOutcomeScenario[i]?.inLof ?? 0,
              ),
              subGroup: "Post-Mitigation",
            },
            {
              label: `Baseline COF (${overallRiskByOutcomeScenario[i].group})`,
              value: riskUnitFormatter(
                overallRiskByOutcomeScenario[i]?.opCof ?? 0,
              ),
              subGroup: "Baseline",
            },
            {
              label: `Post-Mitigation COF (${overallRiskByOutcomeScenario[i].group})`,
              value: riskUnitFormatter(
                overallRiskByOutcomeScenario[i]?.inCof ?? 0,
              ),
              subGroup: "Post-Mitigation",
            },
          ]);
      }

      return {
        mitigationRiskByThreatSeries,
        mitigationRiskByConsequenceSeries,
        mitigationRiskByOutcomeScenarioSeries,
        mitigationRiskDataKeys: ["Baseline", "Post-Mitigation"],
      };
    }

    return {
      mitigationRiskByThreatSeries: [],
      mitigationRiskByConsequenceSeries: [],
      mitigationRiskByOutcomeScenarioSeries: [],
      mitigationRiskDataKeys: [],
    };
  }, [
    overallRiskChartsQueryResult.data?.riskManagementChartsForWellNode,
    appliedWellName,
    appliedHierarchyName,
    riskMitigationName,
    riskUnitFormatter,
  ]);

  return {
    overallRiskChartsQueryResult,
    mitigationRiskByThreatSeries,
    mitigationRiskByConsequenceSeries,
    mitigationRiskByOutcomeScenarioSeries,
    mitigationRiskDataKeys,
  };
};
